import styled from "@emotion/styled"
import { useViewport } from "contexts/Viewport"
import getMediaQuery from "css/breakpoints"
import { buttonFilled } from "css/buttons"
import { AnimatePresence, motion } from "framer-motion"
import parse from "html-react-parser"
import { DateTime } from "luxon"
import { useState } from "react"

import { Icon } from "components/icon/Icon"
import { useConsole } from "contexts/Console"
import { useDictionary } from "contexts/Dictionary"
import { useLocale } from "contexts/Locale"
const ButtonIcon = ({ initial, style, animate, variants }) => {
  return (
    <BtnIconWrapper initial={initial} animate={animate} style={style} variants={variants} role='img' aria-hidden='true' alt=''>
      <Icon type='chevron' />
    </BtnIconWrapper>
  )
}
const Wrapper = styled.div`
  position: relative;
  margin: 20px 4% 0;
  ${getMediaQuery("m")} {
    margin-top: 24px;
  }
  display: flex;
  align-items: baseline;
  justify-content: center;
  text-align: center;
  grid-column: doc-start / doc-end;
  grid-row: 5;
  z-index: 3;
  margin-block-end: var(--m-h-space);
`

const OpeningButtonWrapper = styled.div`
  ${({ btnon }) => (btnon === "true" ? `cursor:pointer ; pointer-events:auto;` : `pointer-events:none !important; cursor:auto !important; `)}
  z-index: 2;
`

const OpeningButton = styled(motion.button)`
  margin-inline: auto;

  ${buttonFilled}
  background: rgb(var(--light-grey));
  color: rgb(var(--green));
  z-index: 2;

  svg {
    margin-left: 0px;
    margin-inline-start: 0 !important;
  }
`
const BtnIconWrapper = styled(motion.div)`
  position: relative !important;
  clip-path: none !important;
  right: 0px;
  width: unset !important;
  height: unset !important;
`

const OpeningWrapper = styled(motion.ul)`
  position: absolute;
  display: flex;
  flex-direction: column;
  background: rgb(var(--light-grey));
  width: 100%;
  max-width: 300px;

  ${getMediaQuery("m")} {
    max-width: 380px;
    &.hoursgotpause {
      max-width: 428px;
    }
    margin-inline: auto;
  }
  border-radius: 20px;
  padding: 44px 32px 26px;
`

const OpeningWrapperHidden = styled(motion.ul)`
  position: absolute;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  margin-block-start: 10px;
`

const StyledListItem = styled(motion.li)`
  /* display: flex; */
  display: grid;
  gap: 1rem;
  grid-template-columns: minmax(120px, max-content) minmax(160px, max-content);
  column-gap: 40px;

  flex-direction: row;
  font-weight: normal;
  align-items: start;
  width: 100%;
  margin: 0 0 1rem;
  color: rgb(var(--light-black));
  &.selected {
    color: rgb(var(--green));
    span:last-of-type {
      font-weight: normal;
    }
  }
  text-wrap: pretty;
  span:last-of-type {
    white-space: break-spaces;
    /* white-space: nowrap; */
    text-align: left;
    line-height: 1.3em;
    font-weight: lighter;
  }

  ${getMediaQuery("m")} {
    grid-template-columns: minmax(120px, max-content) minmax(160px, max-content);
    column-gap: 100px;
  }
`
const DayName = styled.span`
  justify-self: flex-start;
  font-weight: bold;
  .selected & {
    ::after {
      content: "";
      display: inline-block;
      opacity: 1;
      margin-bottom: 2px;
      -webkit-margin-start: 10px;
      margin-inline-start: 10px;
      border-radius: 50%;
      width: 6px;
      height: 6px;
      background: rgb(var(--green));
    }
  }
  /* ${getMediaQuery("m")} {
    padding-inline-start: 2rem;
  } */
`
const OpeningDetails = styled(motion.li)`
  display: flex;
  flex-direction: column;
  text-align: left;
  margin-top: 14px;

  &.has-open-time {
    margin-top: 0px;
    &::before {
      content: "";
      position: relative;
      width: 100%;
      height: 1px;
      background: rgb(var(--grey));
      margin: 18px 0;
    }
  }
`

const HoursDetails = ({ day, index, isOpen, ...rest }) => {
  const { hoursDayTranslated, hoursDayTranslatedRtl } = rest
  const isRTL = false // #todov7
  const hoursDetail = isRTL
    ? hoursDayTranslatedRtl?.replaceAll(" - ", "&nbsp;-&nbsp;").replaceAll(", ", "<br>")
    : hoursDayTranslated?.replaceAll(" - ", "&nbsp;-&nbsp;").replaceAll(", ", "<br>")
  const t = useDictionary()
  const dayTranslated = [t.monday(), t.tuesday(), t.wednesday(), t.thursday(), t.friday(), t.saturday(), t.sunday()]
  //const { t } = useTranslation()
  const variants = {
    initial: {
      y: -10,
      opacity: 0,
    },
    open: {
      y: 0,
      opacity: 1,
      transition: {
        y: { stiffness: 1000, velocity: 100 },
      },
    },
    closed: {
      y: 10,
      opacity: 0,
      transition: {
        y: { stiffness: 1000 },
      },
    },
  }
  return (
    <StyledListItem
      variants={variants}
      className={index === day - 1 ? "selected" : ""}
      aria-current={index === day - 1 ? "date" : false}
      key={"day-" + (index + 1).toString()}
    >
      <DayName>{dayTranslated[index]}</DayName>
      <span>{hoursDetail !== "" ? parse(hoursDetail) : t.closed()}</span>
    </StyledListItem>
  )
}

function OpeningHours({ openingTimes, openingDetail, timezone, timeFormat }) {
  const console = useConsole()
  const t = useDictionary()
  const [isOpen, setIsOpen] = useState(false)
  const date = DateTime.utc().setZone(timezone.timeZoneId)
  const [day] = useState(date.toFormat("c"))
  const [hasOpeningTime] = useState(openingTimes && openingTimes.map((item, i) => (item.hoursDayTranslated !== "" ? true : false)))
  const [isClosed] = useState(openingTimes[day - 1].isClosed)
  const [btnon] = useState(isClosed && openingDetail === "" ? false : true)
  const [hoursgotpause] = useState(openingTimes[day - 1].close2 !== "")
  const { isMobile } = useViewport()
  const locale = useLocale()
  const lang = locale.current.codes.ws

  const OpeningVariant = {
    initial: {
      width: "200px",
      height: "44px",
      padding: 0,
      borderRadius: "50px",
    },
    open: {
      width: isMobile.get() ? "calc(100% - 40px)" : "calc(100% - 128px)",
      padding: isMobile.get() ? "64px 20px 20px" : "64px 64px 26px",
      height: "auto",
      borderRadius: "20px",
      transition: {
        delayChildren: 0.4,
        staggerChildren: 0.05,
        staggerDirection: 1,
        duration: 0.7,
        width: { duration: 0.4 },
        height: { delay: 0.2, duration: 0.5 },
        borderRadius: { duration: 0.2 },
      },
    },
    closed: {
      width: "200px",
      height: "44px",
      padding: 0,
      borderRadius: "50px",
      transition: {
        staggerChildren: 0,
        staggerDirection: 0,
        duration: 0.5,
        delay: 0.6,
        borderRadius: { delay: 0.65, duration: 0.3 },
        width: { delay: 0.6, duration: 0.5 },
        height: { delay: 0.2, duration: 0.4 },
      },
    },
  }
  const handleClick = () => {
    setIsOpen(!isOpen)
  }
  const ArrowVariants = {
    open: {
      rotate: -90,
    },
    closed: {
      rotate: 90,
    },
  }
  const openingVariants = {
    initial: {
      y: -10,
      opacity: 0,
    },
    open: {
      y: 0,
      opacity: 1,
      transition: {
        y: { stiffness: 1000, velocity: 100 },
      },
    },
    closed: {
      y: 10,
      opacity: 0,
      transition: {
        y: { stiffness: 1000 },
      },
    },
  }
  function getOpenHours(timezone, timeFormat, lang = "en") {
    let isStoreOpen

    const format = timeFormat ? timeFormat : "h:mm a"

    const open1 = openingTimes[day - 1].open1
    const open2 = openingTimes[day - 1].open2
    const open3 = openingTimes[day - 1].open3
    const close1 = openingTimes[day - 1].close1
    const close2 = openingTimes[day - 1].close2
    const close3 = openingTimes[day - 1].close3

    const currentDateTime = DateTime.utc()

    const dateOpen1 = currentDateTime.setZone(timezone.timeZoneId).set(DateTime.fromFormat(open1, "T").toObject())
    const dateOpen2 = open2 !== "" ? currentDateTime.setZone(timezone.timeZoneId).set(DateTime.fromFormat(open2, "T").toObject()) : null
    const dateOpen3 = open3 !== "" ? currentDateTime.setZone(timezone.timeZoneId).set(DateTime.fromFormat(open3, "T").toObject()) : null
    const dateClose1 = currentDateTime.setZone(timezone.timeZoneId).set(DateTime.fromFormat(close1, "T").toObject())
    const dateClose2 = close2 !== "" ? currentDateTime.setZone(timezone.timeZoneId).set(DateTime.fromFormat(close2, "T").toObject()) : null
    const dateClose3 = close3 !== "" ? currentDateTime.setZone(timezone.timeZoneId).set(DateTime.fromFormat(close3, "T").toObject()) : null
    const dateNow = lang !== "ja" ? date.setLocale(lang) : date

    isStoreOpen =
      dateOpen3 != null
        ? (dateOpen1.ts < dateNow.ts && dateNow.ts < dateClose1.ts) ||
        (dateOpen2.ts < dateNow.ts && dateNow.ts < dateClose2.ts) ||
        (dateOpen3.ts < dateNow.ts && dateNow.ts < dateClose3.ts)
        : dateOpen2 != null
          ? (dateOpen1.ts < dateNow.ts && dateNow.ts < dateClose1.ts) || (dateOpen2.ts < dateNow.ts && dateNow.ts < dateClose2.ts)
          : dateOpen1.ts < dateNow.ts && dateNow.ts < dateClose1.ts

    console.verbose(
      "🚀 ~ file: TimeDisplay.js ~ line 134 ~ getOpenHours ~ isStoreOpen",
      "Open1:",
      dateOpen1.toFormat("T"),
      dateOpen1.ts,
      "|| Close1:",
      dateClose1.toFormat("T"),
      dateClose1.ts,
      "|| Open2:",
      dateOpen2 && dateOpen2.toFormat("T"),
      dateOpen2 && dateOpen2.ts,
      "|| Close2:",
      dateClose2 && dateClose2.toFormat("T"),
      dateClose2 && dateClose2.ts,
      "|| Open3:",
      dateOpen3 && dateOpen3.toFormat("T"),
      dateOpen3 && dateOpen3.ts,
      "|| Close3:",
      dateClose3 && dateClose3.toFormat("T"),
      dateClose3 && dateClose3.ts,
      "|| dateNow:",
      dateNow.toFormat("T"),
      dateNow.ts,
      "|| isStoreOpen:",
      isStoreOpen
    )

    return isStoreOpen
  }

  return (
    <Wrapper>
      {(hasOpeningTime || openingDetail !== "") && (
        <OpeningButtonWrapper btnon={btnon.toString()}>
          <OpeningButton onClick={handleClick} aria-expanded={isOpen} aria-controls='opening-hours'>
            {isClosed
              ? t.temporarilyClosed()
              : hasOpeningTime
                ? getOpenHours(timezone, timeFormat, lang)
                  ? t.openNow()
                  : t.closedNow()
                : isOpen
                  ? t.openingDetailsHide()
                  : t.openingDetailsShow()}
            {btnon && <ButtonIcon animate={isOpen ? "open" : "closed"} variants={ArrowVariants} />}
          </OpeningButton>
        </OpeningButtonWrapper>
      )}
      <AnimatePresence mode='wait'>
        {isOpen && (
          <OpeningWrapper
            initial='initial'
            animate={isOpen ? "open" : "closed"}
            exit='closed'
            variants={OpeningVariant}
            className={hoursgotpause ? "hours-got-pause" : ""}
            // hoursgotpause={hoursgotpause.toString()}
            id='opening-hours'
          >
            {hasOpeningTime && !isClosed && openingTimes.map((item, i) => <HoursDetails day={day} key={i} index={i} isOpen={isOpen} {...item}></HoursDetails>)}
            {openingDetail !== "" && (
              <OpeningDetails hasOpeningTime={hasOpeningTime} variants={openingVariants}>
                {openingDetail}
              </OpeningDetails>
            )}
          </OpeningWrapper>
        )}
      </AnimatePresence>
      {/* opening times need to be in the DOM in the opposite case for a11y concerns */}
      <AnimatePresence>
        {!isOpen && (
          <OpeningWrapperHidden id='opening-hours'>
            {hasOpeningTime && !isClosed && openingTimes.map((item, i) => <HoursDetails day={day} key={i} index={i} isOpen={isOpen} {...item}></HoursDetails>)}
            {openingDetail !== "" && (
              <OpeningDetails className={hasOpeningTime ? "has-open-time" : ""} variants={openingVariants}>
                {openingDetail}
              </OpeningDetails>
            )}
          </OpeningWrapperHidden>
        )}
      </AnimatePresence>
    </Wrapper>
  )
}

export default OpeningHours
